import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TbCircleCheck, TbAlertTriangle, TbCircleX, TbShieldCheck, TbBulb, TbInfoCircle } from "react-icons/tb";
import { displayCode, requiredCodesLabel } from "src/helpers/goaRules";
import type { GoaFinding } from "./goaCheck";
import type { Suggestion, Suggestions } from "./suggestions";
import { formatDay } from "./treatmentRules";

export type CheckState = "ok" | "warn" | "error";

export interface CheckItem {
    key: string;
    state: CheckState;
    title: string;
    detail: string;
    /** Step to open with "Ändern" (index in CREATE_STEPS). */
    stepIndex: number;
}

const ICONS = { ok: TbCircleCheck, warn: TbAlertTriangle, error: TbCircleX };

/** Step 4: everything checked before creating, one row per area, each with a way back to fix it —
 *  then findings to confirm one by one (duplicate codes on every plan, the GOÄ checker's on Pro —
 *  story 15), and on Plus+ suggestions to apply or dismiss (story 16b). */
export function StepCheck({
    items,
    onOpenStep,
    goa,
    suggestions,
}: {
    items: CheckItem[];
    onOpenStep: (index: number) => void;
    /** The findings and their confirmations; `upgrade` on plans without the GOÄ checker. */
    goa: { findings: GoaFinding[]; acked: Set<string>; onToggle: (key: string) => void; upgrade: boolean };
    /** Plus+: suggestions (story 16b); `upgrade` on Basic. */
    suggestions: {
        result: Suggestions;
        dismissed: Set<string>;
        onApply: (s: Suggestion) => void;
        onDismiss: (id: string) => void;
        codeName: (code: string) => string | undefined;
    } | "upgrade";
}) {
    const { t } = useTranslation();
    const blocked = items.some((i) => i.state === "error");
    const { findings } = goa;
    const pending = findings.some((f) => !goa.acked.has(f.key));

    return (
        <div className="ic-check">
            <p className={`ic-check__verdict ic-check__verdict--${blocked ? "error" : pending ? "warn" : "ok"}`} role="status">
                {blocked ? t("invoiceCreate.check.blocked") : pending ? t(`invoiceCreate.check.${goa.upgrade ? "billingNotesPending" : "goaPending"}`) : t("invoiceCreate.check.ready")}
            </p>
            <ul className="ic-check__list">
                {items.map((item) => {
                    const Icon = ICONS[item.state];
                    return (
                        <li key={item.key} className={`ic-check__item ic-check__item--${item.state}`}>
                            <Icon className="ic-check__icon" aria-hidden />
                            <div className="ic-check__text">
                                <span className="ic-check__title">{item.title}</span>
                                <span className="ic-check__detail">{item.detail}</span>
                            </div>
                            <button type="button" className="ic-check__change" onClick={() => onOpenStep(item.stepIndex)}>
                                {t("invoiceCreate.change")}
                            </button>
                        </li>
                    );
                })}
            </ul>

            {goa.upgrade && (
                <p className="ic-check__upgrade">
                    <TbShieldCheck aria-hidden /> {t("invoiceCreate.check.goaUpgrade")}
                </p>
            )}
            {findings.length > 0 && (
                <section className="ic-goa" aria-labelledby="ic-goa-title">
                    <h3 id="ic-goa-title" className="ic-goa__title">{t(`invoiceCreate.check.${goa.upgrade ? "billingNotes" : "goaFindingsTitle"}`)}</h3>
                    <p className="ic-goa__lead">{t(`invoiceCreate.check.${goa.upgrade ? "billingNotesLead" : "goaFindingsLead"}`)}</p>
                    <ul className="ic-goa__list">
                        {findings.map((f) => (
                            <li key={f.key} className={`ic-goa__item${goa.acked.has(f.key) ? " ic-goa__item--acked" : ""}`}>
                                <div className="ic-goa__text">
                                    <span className="ic-goa__what">{findingText(f, t)}</span>
                                    {f.note && <span className="ic-goa__note">{f.note}</span>}
                                </div>
                                <label className="ic-goa__ack">
                                    <input type="checkbox" checked={goa.acked.has(f.key)} onChange={() => goa.onToggle(f.key)} />
                                    <span>{t("invoiceCreate.check.goaAck")}</span>
                                </label>
                            </li>
                        ))}
                    </ul>
                    <button type="button" className="ic-check__change ic-goa__fix" onClick={() => onOpenStep(2)}>
                        {t("invoiceCreate.check.goaFix")}
                    </button>
                </section>
            )}

            {suggestions === "upgrade" ? (
                <p className="ic-check__upgrade">
                    <TbBulb aria-hidden /> {t("invoiceCreate.suggestions.upgrade")}
                </p>
            ) : (
                <SuggestionList {...suggestions} />
            )}

            <section className="ic-note ic-note--neutral ic-legal" aria-labelledby="ic-legal-title">
                <TbInfoCircle aria-hidden />
                <div className="ic-legal__text">
                    <h3 id="ic-legal-title" className="ic-legal__title">{t("invoiceCreate.check.legal.title")}</h3>
                    <p>{t(`invoiceCreate.check.legal.${goa.upgrade ? "intro" : "introChecker"}`)}</p>
                    <p>{t("invoiceCreate.check.legal.responsibility")}</p>
                    <p>{t("invoiceCreate.check.legal.liability")}</p>
                </div>
            </section>
        </div>
    );
}

/** Suggestions shown before "Alle anzeigen" — the server sends up to 10, most relevant first. */
export const SUGGESTIONS_SHOWN = 3;

export type SuggestionListProps = {
    result: Suggestions;
    dismissed: Set<string>;
    onApply: (s: Suggestion) => void;
    onDismiss: (id: string) => void;
    /** The GOÄ catalog's description of a code — not everyone knows every number. */
    codeName: (code: string) => string | undefined;
    /** `invoiceCreate.suggestions.<titleKey>` — step 3 shows only the diagnoses' codes under its own title. */
    titleKey?: string;
};

export function SuggestionList({ result, dismissed, onApply, onDismiss, codeName, titleKey = "title" }: SuggestionListProps) {
    const { t } = useTranslation();
    const [showAll, setShowAll] = useState(false);
    if (result.status !== "done") return null;
    const open = result.suggestions.filter((s) => !dismissed.has(s.id));
    if (open.length === 0) return null;
    const shown = showAll ? open : open.slice(0, SUGGESTIONS_SHOWN);
    const sg = (key: string, opts?: Record<string, unknown>) => t(`invoiceCreate.suggestions.${key}`, opts);

    return (
        <section className="ic-suggestions" aria-labelledby="ic-suggestions-title">
            <h3 className="ic-suggestions__title">
                <TbBulb aria-hidden /> <span id="ic-suggestions-title">{sg(titleKey)}</span>
                <span className="ic-suggestions__count">{open.length}</span>
            </h3>
            <p className="ic-suggestions__basis">{sg(`basis.${result.basis}`, { count: result.invoiceCount })}</p>
            <ul className="ic-suggestions__list">
                {shown.map((s) => {
                    const params = formatParams(s.reason.params);
                    const name = codeName(s.code);
                    return (
                        <li key={s.id} className="ic-suggestions__item">
                            <div className="ic-suggestions__text">
                                <span className="ic-suggestions__what">
                                    {sg(`kind.${s.kind}`, { code: displayCode(s.code), date: formatDay(s.date), factor: fmtFactor(s.factor), current: params.current })}
                                </span>
                                {name && <span className="ic-suggestions__name">{name}</span>}
                                <span className="ic-suggestions__why">{sg(`reasons.${s.reason.key}`, params)}</span>
                            </div>
                            <div className="ic-suggestions__actions">
                                <button type="button" className="ic-suggestions__apply" onClick={() => onApply(s)}>
                                    {sg("apply")}
                                </button>
                                <button type="button" className="ic-suggestions__dismiss" onClick={() => onDismiss(s.id)}>
                                    {sg("dismiss")}
                                </button>
                            </div>
                        </li>
                    );
                })}
            </ul>
            {open.length > SUGGESTIONS_SHOWN && (
                <button type="button" className="ic-check__change ic-suggestions__more" onClick={() => setShowAll((v) => !v)} aria-expanded={showAll}>
                    {showAll ? sg("showFewer") : sg("showAll", { count: open.length })}
                </button>
            )}
        </section>
    );
}

const fmtFactor = (n: unknown) => (typeof n === "number" ? n.toLocaleString("de-DE", { maximumFractionDigits: 2 }) : "");

/** Codes without technical prefixes, factors the German way. */
function formatParams(params: Record<string, string | number>) {
    const out: Record<string, string | number> = { ...params };
    for (const k of ["code", "base", "other"]) if (typeof out[k] === "string") out[k] = displayCode(out[k] as string);
    for (const k of ["factor", "current"]) if (typeof out[k] === "number") out[k] = fmtFactor(out[k]);
    return out;
}

function findingText(f: GoaFinding, t: (key: string, opts?: Record<string, unknown>) => string) {
    const date = f.date ? formatDay(f.date) : t("invoiceCreate.check.goaNoDate");
    if (f.kind === "duplicate") return t("invoiceCreate.check.goaDuplicate", { nr: displayCode(f.nr), count: f.count, date });
    if (f.kind === "singleRate") return t("invoiceCreate.check.goaSingleRate", { nr: displayCode(f.nr), factor: f.factor.toLocaleString("de-DE"), date });
    if (f.kind === "rule") {
        const { window, detail, per, ...params } = f.params;
        return t(`invoiceCreate.check.rules.${f.type}`, {
            ...params,
            nr: f.codes.map(displayCode).join(", "),
            date,
            window: window ? t(`invoiceCreate.check.ruleWindow.${window}`) : "",
            detail: detail ? t(`invoiceCreate.check.ruleDetail.${detail}`) : "",
            per: per ? t(`invoiceCreate.check.rulePer.${per}`) : "",
        });
    }
    if (f.kind === "conflict") return t("invoiceCreate.check.goaConflict", { a: displayCode(f.codes[0]), b: displayCode(f.codes[1]), date });
    if (f.kind === "overLimit") return t("invoiceCreate.check.goaOverLimit", { nr: displayCode(f.nr), billed: f.billed, max: f.max, date });
    return t("invoiceCreate.check.goaMissingCode", { nr: displayCode(f.nr), requires: requiredCodesLabel(f.requires), date });
}
